// @ts-strict-ignore
import {Component, Injector, Renderer2, signal} from "@angular/core";
import {Translations} from "@translations";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {
	AutoLoginCapability,
	DeviceActivityType,
	DisplayAutoLoginCapability,
	DisplayDeviceType,
	FontAwesomeCode,
	Permissions,
	TrainingRoom,
} from "@models";
import {SimMaterialDialogService, SimUserExtendedService, SimUtilityService} from "@services";
import {SelectableTypes} from "./setup-auto-login/setup-auto-login.component";

export interface DialogData_RegisterDeviceDialogInput extends MatDialogDataBaseInput {
	deviceIdToBeMapped: string;
}

export interface DialogData_RegisterDeviceDialogOutput extends MatDialogDataBaseOutput {}

enum VM_AutoLoginCapabilityType {
	EXAM_DEVICE = "EXAM_DEVICE",
	DISPLAY_DEVICE = "DISPLAY_DEVICE",
}

enum VM_DeviceCapabilityType {
	AUTO_LOGIN = "AUTO_LOGIN",
	DEVICE_CAPABILITY = "DEVICE_CAPABILITY",
}

@Component({
	selector: "sim-register-device-dialog",
	templateUrl: "./register-device-dialog.component.html",
	styleUrl: "./register-device-dialog.component.scss",
	standalone: false,
})
export class RegisterDeviceDialogComponent extends BasicMaterialDialog<
	RegisterDeviceDialogComponent,
	DialogData_RegisterDeviceDialogInput,
	DialogData_RegisterDeviceDialogOutput
> {
	public readonly T = Translations;
	public readonly VM_DeviceCapabilityType = VM_DeviceCapabilityType;
	public readonly VM_AutoLoginCapabilityType = VM_AutoLoginCapabilityType;
	public readonly SimUtilityService = SimUtilityService;

	selectedAutoLoginType: VM_AutoLoginCapabilityType = null;
	public configureAutoLoginOrDeviceCapability = signal<VM_DeviceCapabilityType>(null);

	autoLoginTypes: {type: VM_AutoLoginCapabilityType; icon: FontAwesomeCode}[] = [
		{
			type: VM_AutoLoginCapabilityType.EXAM_DEVICE,
			icon: {fontSet: "fad", fontIcon: "fa-cubes"},
		},
		{
			type: VM_AutoLoginCapabilityType.DISPLAY_DEVICE,
			icon: {fontSet: "fad", fontIcon: "fa-tv"},
		},
	];
	public userAuthenticated: boolean = false;

	// INPUT OPTIONS
	public readonly selectableDisplayTypes: SelectableTypes<DisplayDeviceType>[] = [
		{
			type: DisplayDeviceType.FloorScreen,
			icon: {fontSet: "fal", fontIcon: "fa-tv"},
			translationKey: "DISPLAY_TYPE_FloorScreen",
		},
		{
			type: DisplayDeviceType.RoomStateScreen,
			icon: {fontSet: "fal", fontIcon: "fa-tablet-android"},
			translationKey: "DISPLAY_TYPE_RoomStateScreen",
		},
	];

	public readonly selectableExamDeviceTypes: SelectableTypes<DeviceActivityType>[] = [
		{
			type: DeviceActivityType.ExamExaminerPc,
			icon: {fontSet: "fad", fontIcon: "fa-user-md"},
			translationKey: "EXAM_ROLE_ExamExaminerPc",
		},
		{
			type: DeviceActivityType.ExamStudentOutsidePc,
			icon: {fontSet: "fad", fontIcon: "fa-user-graduate"},
			translationKey: "EXAM_ROLE_ExamStudentOutsidePc",
		},
		{
			type: DeviceActivityType.ExamStandardizedPatientPc,
			icon: {fontSet: "fad", fontIcon: "fa-user-injured"},
			translationKey: "EXAM_ROLE_ExamStandardizedPatientPc",
		},
	];

	protected getExamAutologinType(value: TrainingRoom | null, deviceActivityType: DeviceActivityType | null) {
		return {
			type: deviceActivityType,
			roomId: value?.id,
		} as AutoLoginCapability;
	}

	protected getDisplayAutologinType(value: TrainingRoom | null, displayDeviceType: DisplayDeviceType | null) {
		return {
			type: DeviceActivityType.Display,
			roomId: value?.id,
			displayDeviceType: displayDeviceType,
		} as DisplayAutoLoginCapability;
	}

	constructor(
		_injector: Injector,
		private renderer: Renderer2,
		public simUserService: SimUserExtendedService,
		private _simDialogService: SimMaterialDialogService,
	) {
		super(_injector);

		//Check if the current user has the correct ACL to configure the auto-login capability
		if (!simUserService.hasPermission(Permissions.RegisterDevice)) {
			this.openLoginDialog();
		} else {
			this.userAuthenticated = true;
		}

		this.configureAutoLoginOrDeviceCapability.set(VM_DeviceCapabilityType.AUTO_LOGIN); // TODO After Implementing the Device Capability remove static value
	}

	selectDeviceType(type: VM_AutoLoginCapabilityType) {
		this.selectedAutoLoginType = type;
		if (this.selectedAutoLoginType === VM_AutoLoginCapabilityType.EXAM_DEVICE) {
			const matDialogContainer = this.dialogRef._containerInstance["_hostElement"];
			this.renderer.addClass(matDialogContainer, "matDialogFixedHeight90vh");
		}
	}

	setAutoLoginCapability(device_capability: VM_DeviceCapabilityType) {
		this.configureAutoLoginOrDeviceCapability.set(device_capability);
	}

	public openLoginDialog() {
		this._simDialogService.openLoginUserDialog({}).then((dialogResult) => {
			if (dialogResult?.action === "ok") {
				this.userAuthenticated = true;
			}
		});
	}

	openQrCodeDialog() {
		this._simDialogService.openDeviceIDQRCodeDialog({}).then();
	}
}
